import { type GridColDef } from "@mui/x-data-grid-premium";
import React, { useContext, useEffect, useState } from "react";

import { PatientContext } from "../../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../../contexts/ProjectContext.tsx";
import { useFetch } from "../../../services/hooks/useFetch.ts";
import { projectsPrefix } from "../../../utils/main.ts";
import AccordionTable from "../../datagrid/AccordionTable.tsx";

interface OtherDevicesUsedTableProps {
  encounter: EncounterSearchResult | null;
}

export default function OtherDevicesUsedTable({
  encounter,
}: OtherDevicesUsedTableProps) {
  const { id: projectId } = useContext(ProjectContext)!;
  const { id } = useContext(PatientContext)!;

  const [shouldFetchDevices, setShouldFetchDevices] = useState(false);

  const {
    isLoading,
    data: otherDevicesUsed,
    fetch: fetchOtherDevicesUsed,
  } = useFetch<OtherDevice[]>();

  const otherDevicesTableFields: GridColDef<OtherDevice>[] = [
    { field: "manufacturerName", headerName: "MFR", width: 250 },
    { field: "catalogNumber", headerName: "MFR Cat #", width: 100 },
    { field: "supplyName", headerName: "Name", width: 150 },
    { field: "suppliesUsed", headerName: "Used", width: 100, type: "number" },
    {
      field: "suppliesWasted",
      headerName: "Wasted",
      width: 100,
      type: "number",
    },
    { field: "itemTypeName", headerName: "Type", width: 250 },
  ];

  useEffect(() => {
    if (shouldFetchDevices && !isLoading) {
      void fetchOtherDevicesUsed(
        `${projectsPrefix}/${projectId}/patients/${id}/other_devices`,
        {
          responseManipulationFunction: (data) =>
            data?.map((otherDevice: OtherDevice) => ({
              ...otherDevice,
              // Rows need an ID for datagrid filtering
              id: crypto.randomUUID(),
            })),
          postResponseAction: () => {
            setShouldFetchDevices(false);
          },
        },
      );
    }
  }, [projectId, id, shouldFetchDevices, isLoading, fetchOtherDevicesUsed]);

  return (
    <AccordionTable
      loading={isLoading}
      tableId={"other-devices-used"}
      title={"Other Devices Used"}
      totalCount={encounter?.otherDevicesCount || 0}
      columns={otherDevicesTableFields}
      rows={otherDevicesUsed || []}
      triggerFetch={() => setShouldFetchDevices(true)}
      highlightNew={false}
    />
  );
}
